import { FC, ReactNode, useState } from 'react';
import { Popover } from '@mui/material';

import { Text } from '../../../shared/uiComponents';

interface StatusChipColorScheme {
	background: string;
	border: string;
	text: string;
}

interface StatusChipProps {
	colorScheme: StatusChipColorScheme;
	count: number;
	label: string;
	popoverContent: ReactNode;
	subtitle?: string;
}

const StatusChip: FC<StatusChipProps> = ({ colorScheme, count, label, popoverContent, subtitle }) => {
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

	if (count === 0) return null;

	return (
		<>
			<div
				onClick={(e) => setAnchorEl(e.currentTarget)}
				style={{
					display: 'inline-flex',
					flexDirection: subtitle ? 'column' : 'row',
					alignItems: subtitle ? 'flex-start' : 'center',
					backgroundColor: colorScheme.background,
					color: colorScheme.text,
					border: `1px solid ${colorScheme.border}`,
					borderRadius: '4px',
					padding: '4px 10px',
					fontSize: '12px',
					fontWeight: 600,
					lineHeight: '16px',
					cursor: 'pointer',
				}}
			>
				<span>{`${count} ${label}`}</span>
				{subtitle && <span style={{ fontWeight: 400 }}>{subtitle}</span>}
			</div>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
				transformOrigin={{ vertical: 'top', horizontal: 'left' }}
				slotProps={{
					paper: {
						sx: {
							borderRadius: '12px',
							padding: '20px',
							minWidth: '320px',
							maxWidth: '380px',
							boxShadow: '0px 8px 24px rgba(0,0,0,0.12)',
						},
					},
				}}
			>
				<div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
					{popoverContent}
				</div>
			</Popover>
		</>
	);
};

export interface StatusChipItem {
	key: string;
	title: string;
	subtitle: string;
	dotColor?: string;
	extra?: string | string[];
}

export const StatusChipPopoverList: FC<{ heading: string; items: StatusChipItem[]; action?: (key: string) => ReactNode }> = ({
	heading,
	items,
	action,
}) => (
	<>
		<Text title={heading} type="psm" fontWeight="semiBold" />
		{items.map((item) => (
			<div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{item.dotColor && (
						<div
							style={{
								width: 8,
								height: 8,
								borderRadius: '50%',
								backgroundColor: item.dotColor,
								flexShrink: 0,
							}}
						/>
					)}
					<div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
						<Text title={item.title} type="pxs" fontWeight="semiBold" />
						<Text title={item.subtitle} type="pxs" color="#6b7280" />
						{item.extra && (Array.isArray(item.extra) ? item.extra : [item.extra]).map((line, idx) => (
							<Text key={idx} title={line} type="pxs" color="#6b7280" />
						))}
					</div>
				</div>
				{action?.(item.key)}
			</div>
		))}
	</>
);

export default StatusChip;
